import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Switch, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import type { FormField, SurveyDetailUpdate } from 'src/types/survey';

import { styles } from 'src/components/survey/SurveySettings/index.styles';

interface FormFieldEditProps extends FormField {
  index: number;
}

const FormFieldEdit: FC<FormFieldEditProps> = ({
  isActive,
  index,
  isMandatory,
  name,
  description,
}) => {
  const { t } = useTranslation('survey');
  const { errors, handleChange } = useFormikContext<SurveyDetailUpdate>();
  const error = errors?.specificSettings?.followUp?.customFields?.[index];

  return (
    <Box
      sx={styles.formFieldEdit}
      aria-label={t('settings.followUpModule.customFieldEdit')}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Checkbox
          checked={isActive}
          onChange={handleChange}
          name={`specificSettings.followUp.customFields[${index}].isActive`}
          color="primary"
          id={`is-active-${index}`}
        />
        <Typography component="label" htmlFor={`is-active-${index}`}>
          {t('settings.followUpModule.active')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography component="label" htmlFor={`name-${index}`}>
            {t('settings.followUpModule.name')}
          </Typography>
          <TextField
            id={`name-${index}`}
            onChange={handleChange}
            name={`specificSettings.followUp.customFields[${index}].name`}
            value={name}
            placeholder={t('settings.followUpModule.name')}
          />
          {error && (
            <Typography
              variant="body4"
              color="error"
              sx={{
                gridColumn: '2 / 3',
              }}
            >
              {typeof error === 'string' ? error : error.name}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography component="label" htmlFor={`description-${index}`}>
            {t('settings.followUpModule.description')}
          </Typography>
          <TextField
            onChange={handleChange}
            name={`specificSettings.followUp.customFields[${index}].description`}
            value={description}
            id={`description-${index}`}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            onChange={handleChange}
            checked={isMandatory}
            name={`specificSettings.followUp.customFields[${index}].isMandatory`}
            id={`is-mandatory-${index}`}
          />
          <Typography component="label" htmlFor={`is-mandatory-${index}`}>
            {t('settings.followUpModule.mandatory')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FormFieldEdit;
